import type { ReleaseAnnouncements } from '@/viewmodel/shared/versionCheck/types';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface VersionCheckState {
  updateAvailable: boolean;
  latestVersion: string | null;
  dismissed: boolean;
  announcements: ReleaseAnnouncements[];
  announcementsLoaded: boolean;
  seenVersion: string | null;
  panelOpen: boolean;
}

export const versionCheckInitialState: VersionCheckState = {
  updateAvailable: false,
  latestVersion: null,
  dismissed: false,
  announcements: [],
  announcementsLoaded: false,
  seenVersion: null,
  panelOpen: false,
};

const versionCheckSlice = createSlice({
  name: 'versionCheck',
  initialState: versionCheckInitialState,
  reducers: {
    updateDetected: (state, action: PayloadAction<string | null>) => {
      state.updateAvailable = true;
      state.latestVersion = action.payload;
      state.dismissed = false;
    },
    updateDismissed: (state) => {
      state.dismissed = true;
    },
    announcementsReceived: (
      state,
      action: PayloadAction<{ releases: ReleaseAnnouncements[]; seenVersion: string | null }>,
    ) => {
      state.announcements = action.payload.releases;
      state.seenVersion = action.payload.seenVersion;
      state.announcementsLoaded = true;
    },
    panelOpened: (state, action: PayloadAction<{ seenVersion: string }>) => {
      state.panelOpen = true;
      state.seenVersion = action.payload.seenVersion;
    },
    panelClosed: (state) => {
      state.panelOpen = false;
    },
  },
});

export const { updateDetected, updateDismissed, announcementsReceived, panelOpened, panelClosed } =
  versionCheckSlice.actions;
export default versionCheckSlice.reducer;

type VersionCheckRootState = { versionCheck: VersionCheckState };

export const selectUpdateAvailable = (state: VersionCheckRootState): boolean =>
  state.versionCheck.updateAvailable && !state.versionCheck.dismissed;

export const selectCurrentAnnouncements = (
  state: VersionCheckRootState,
): ReleaseAnnouncements | null => {
  const running = import.meta.env.VITE_APP_VERSION;
  if (running) {
    return state.versionCheck.announcements.find((r) => r.version === running) ?? null;
  }
  return import.meta.env.DEV ? (state.versionCheck.announcements[0] ?? null) : null;
};

export const selectHasUnseenAnnouncements = (state: VersionCheckRootState): boolean => {
  const current = selectCurrentAnnouncements(state);
  return current !== null && state.versionCheck.seenVersion !== current.version;
};

export const selectAnnouncementPanelOpen = (state: VersionCheckRootState): boolean =>
  state.versionCheck.panelOpen;
